import React from 'react'
import { motion } from 'framer-motion'
import { 
  Calendar, MapPin, Clock, Star, ArrowRight, Users, Music, 
  Ticket, Heart, Share2, ExternalLink, Volume2 
} from 'lucide-react'

const EventsRail = () => {
  const [selectedGenre, setSelectedGenre] = React.useState('All')

  const genres = ['All', 'Rock', 'Jazz', 'Electronic', 'Folk', 'Hip Hop', 'Classical']
  
  const weekEvents = [
    {
      id: 1,
      title: 'Summer Music Festival',
      artist: 'Multiple Artists',
      venue: 'Century II',
      date: 'Jul 15',
      time: '6:00 PM',
      price: '$45',
      originalPrice: '$65',
      rating: 4.8,
      attendees: 234,
      image: 'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=800',
      genre: 'Festival',
      featured: true,
      status: 'hot',
      description: 'Three stages of incredible local and regional talent',
      ageLimit: '18+',
      lineup: ['Thunder Road', 'Sarah Mitchell', 'Electric Dreams']
    },
    {
      id: 2,
      title: 'Acoustic Storytellers',
      artist: 'Sarah Mitchell & Friends',
      venue: 'The Cotillion',
      date: 'Jul 18',
      time: '8:00 PM',
      price: '$25',
      rating: 4.9,
      attendees: 89,
      image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=800',
      genre: 'Folk',
      status: 'new',
      description: 'Intimate evening of original songs and storytelling',
      ageLimit: 'All Ages'
    },
    {
      id: 3,
      title: 'Rock the Rafters',
      artist: 'Thunder Road',
      venue: 'Lava Tonic',
      date: 'Jul 20',
      time: '9:00 PM',
      price: '$30',
      rating: 4.7,
      attendees: 156,
      image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
      genre: 'Rock',
      status: 'selling-fast',
      description: 'High-energy rock that will shake the foundation',
      ageLimit: '21+'
    },
    {
      id: 4,
      title: 'Midnight Jazz Session',
      artist: 'Wichita Jazz Collective',
      venue: 'The Wave',
      date: 'Jul 22',
      time: '11:30 PM',
      price: '$20',
      rating: 4.8,
      attendees: 67,
      image: 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=800',
      genre: 'Jazz',
      description: 'Late-night improvisation and smooth vibes',
      ageLimit: '21+'
    },
    {
      id: 5,
      title: 'Electronic Underground',
      artist: 'DJ Nexus & Electric Dreams',
      venue: 'Roxy\'s Downtown',
      date: 'Jul 25',
      time: '10:00 PM',
      price: '$35',
      rating: 4.6,
      attendees: 198,
      image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
      genre: 'Electronic',
      status: 'vip-available',
      description: 'Cutting-edge beats and immersive visuals',
      ageLimit: '18+'
    }
  ]

  const statusBadges = {
    hot: { color: 'bg-red-500', text: 'HOT', pulse: true },
    new: { color: 'bg-green-500', text: 'NEW', pulse: false },
    'selling-fast': { color: 'bg-orange-500', text: 'SELLING FAST', pulse: true },
    'vip-available': { color: 'bg-purple-500', text: 'VIP TIX', pulse: false }
  }

  return (
    <section className="section-padding py-24 bg-gradient-to-b from-transparent via-le-darker/20 to-transparent">
      <div className="container-max">
        {/* Section Header */}
        <motion.div
          className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="mb-8 lg:mb-0">
            <h2 className="text-5xl lg:text-6xl font-display font-bold text-white mb-4">
              This Week's
              <span className="text-gradient block">Must-See Shows</span>
            </h2>
            <p className="text-xl text-white/70 max-w-2xl">
              Handpicked performances that showcase the best of Wichita's live music scene
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button className="btn-secondary flex items-center space-x-2 text-lg px-6 py-3">
              <Calendar size={20} />
              <span>Full Calendar</span>
            </button>
            <button className="btn-primary flex items-center space-x-2 text-lg px-6 py-3">
              <Ticket size={20} />
              <span>Get Tickets</span>
            </button>
          </div>
        </motion.div>

        {/* Genre Filter */}
        <motion.div
          className="flex space-x-3 mb-12 overflow-x-auto pb-2"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {genres.map((genre) => (
            <button
              key={genre}
              onClick={() => setSelectedGenre(genre)}
              className={`px-6 py-3 rounded-xl font-medium whitespace-nowrap transition-all duration-300 ${
                selectedGenre === genre
                  ? 'bg-gradient-to-r from-le-accent to-purple-600 text-white shadow-lg shadow-le-accent/30'
                  : 'glass-card text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {genre}
            </button>
          ))}
        </motion.div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {weekEvents.map((event, index) => (
            <motion.div
              key={event.id}
              className={`glass-card overflow-hidden card-hover group relative ${
                event.featured ? 'lg:col-span-2' : ''
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Status Badge */}
              {event.status && statusBadges[event.status] && (
                <div className={`absolute top-4 left-4 z-10 ${statusBadges[event.status].color} text-white text-xs px-3 py-2 rounded-full font-bold ${
                  statusBadges[event.status].pulse ? 'animate-pulse' : ''
                }`}>
                  {statusBadges[event.status].text}
                </div>
              )}

              {/* Featured Badge */}
              {event.featured && (
                <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-le-accent to-le-accent-2 text-white text-xs px-4 py-2 rounded-full font-bold">
                  FEATURED
                </div>
              )}

              <div className="relative h-64 overflow-hidden">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
                
                {/* Overlay Info */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-black/60 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full border border-white/20">
                      {event.genre}
                    </span>
                    <div className="flex items-center space-x-1 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                      <Star size={14} className="text-yellow-400 fill-current" />
                      <span className="text-white text-sm font-medium">{event.rating}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons Overlay */}
                <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button className="w-10 h-10 glass-card rounded-full flex items-center justify-center text-white hover:text-red-400 transition-colors duration-200">
                    <Heart size={16} />
                  </button>
                  <button className="w-10 h-10 glass-card rounded-full flex items-center justify-center text-white hover:text-le-accent-2 transition-colors duration-200">
                    <Share2 size={16} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-display font-bold text-white mb-2 group-hover:text-le-accent transition-colors duration-300">
                      {event.title}
                    </h3>
                    <p className="text-white/80 font-medium mb-1">{event.artist}</p>
                    <p className="text-white/60 text-sm leading-relaxed">{event.description}</p>
                  </div>
                </div>
                
                {/* Event Details Grid */}
                <div className="grid grid-cols-2 gap-4 text-sm text-white/70 mb-6">
                  <div className="flex items-center space-x-2">
                    <MapPin size={16} className="text-le-accent-2" />
                    <span>{event.venue}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar size={16} className="text-le-accent" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock size={16} className="text-le-amber" />
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users size={16} className="text-green-400" />
                    <span>{event.attendees} going</span>
                  </div>
                </div>

                {/* Lineup Preview */}
                {event.lineup && (
                  <div className="mb-6">
                    <div className="text-sm text-white/60 mb-2">Lineup:</div>
                    <div className="flex flex-wrap gap-2">
                      {event.lineup.slice(0, 3).map((artist) => (
                        <span
                          key={artist}
                          className="bg-white/10 text-white/80 text-xs px-3 py-1 rounded-full"
                        >
                          {artist}
                        </span>
                      ))}
                      {event.lineup.length > 3 && (
                        <span className="text-white/50 text-xs px-3 py-1">
                          +{event.lineup.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Price and Actions */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl font-bold text-le-accent">{event.price}</div>
                      {event.originalPrice && (
                        <div className="text-white/40 text-lg line-through">{event.originalPrice}</div>
                      )}
                    </div>
                    <div className="text-xs text-white/60 mt-1">{event.ageLimit}</div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button className="btn-primary px-6 py-3 flex items-center space-x-2">
                      <Ticket size={18} />
                      <span>Tickets</span>
                    </button>
                    <button className="btn-secondary px-4 py-3">
                      <ExternalLink size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="glass-card p-8 inline-block">
            <h3 className="text-2xl font-display font-semibold text-white mb-4">
              150+ More Events This Month
            </h3>
            <p className="text-white/70 mb-6">
              From intimate acoustic sets to massive festival stages
            </p>
            <button className="btn-secondary text-lg px-8 py-4 flex items-center space-x-2 mx-auto">
              <Calendar size={20} />
              <span>View Full Calendar</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default EventsRail